/**
 * Auth API Client
 *
 * Provides interface and real implementation for authentication operations
 * via the BFF (Backend-for-Frontend) Token Handler.
 *
 * Endpoints:
 * - POST /oauth-agent/login/start - Initiate login, get authorization URL
 * - GET /oauth-agent/claims - Get standard OIDC identity claims from ID token
 * - GET /oauth-agent/userinfo - Get userinfo from ZITADEL
 * - POST /oauth-agent/refresh - Refresh access token
 * - POST /oauth-agent/logout - Initiate logout, get end-session URL
 * - GET /api/tenant-management/me - Get identity + org context (v2 contract)
 *
 * Note: getIdentity() targets the tenant-management backend (proxied by the BFF
 * under /api/*), not an /oauth-agent/* endpoint. It is grouped here because it
 * is the SPA's source of identity/authorization state, which the auth store
 * owns. Under the v2 token contract the browser parses no token for org context
 * — it reads it from this endpoint.
 *
 * @module api/AuthApiClient
 */

import type {
  Claims,
  Identity,
  Userinfo,
  LoginStartResponse,
  RefreshResponse,
  LogoutResponse,
} from '../types';
import { httpGet, httpPost, type HttpOptions } from './http';

// =============================================================================
// Interface
// =============================================================================

/**
 * Interface for authentication API operations.
 *
 * All methods use the BFF's /oauth-agent/* endpoints.
 * The Real implementation makes actual HTTP calls; the Mock
 * implementation returns configurable fixtures for testing.
 */
export interface AuthApiClient {
  /**
   * Initiate the login flow by getting an authorization URL.
   *
   * @param returnTo - URL to redirect back to after successful login
   * @returns Response containing the authorization URL for redirect
   * @throws HttpError on 400 (invalid returnTo) or other errors
   */
  loginStart(returnTo: string): Promise<LoginStartResponse>;

  /**
   * Get the standard OIDC identity claims from the current session's ID token.
   *
   * @returns The JWT claims object (identity fields only under the v2 contract)
   * @throws HttpError with status 401 if not authenticated
   */
  getClaims(): Promise<Claims>;

  /**
   * Get the caller's identity and org context from the tenant-management
   * backend (`GET /api/tenant-management/me`). This is the SPA's source for the
   * active org, roles, and the admin/staff flags — the browser parses no token.
   *
   * @returns The identity object
   * @throws HttpError with status 401 if not authenticated
   * @throws HttpError with status 502 if the backend is unreachable
   */
  getIdentity(): Promise<Identity>;

  /**
   * Get the userinfo from ZITADEL for the current session.
   *
   * @returns The userinfo object
   * @throws HttpError with status 401 if not authenticated
   * @throws HttpError with status 502 if upstream error
   */
  getUserInfo(): Promise<Userinfo>;

  /**
   * Refresh the access token using the refresh token.
   *
   * @returns Response with loggedIn status and new expiry
   * @throws HttpError with status 401 if session is dead (must re-login)
   * @throws HttpError with status 502 if transient upstream error
   */
  refresh(): Promise<RefreshResponse>;

  /**
   * Initiate the logout flow by getting an end-session URL.
   *
   * @param returnTo - URL to redirect back to after logout completes
   * @returns Response containing the end-session URL for redirect
   * @throws HttpError on 400 (invalid returnTo) or other errors
   */
  logout(returnTo: string): Promise<LogoutResponse>;
}

// =============================================================================
// Request Types
// =============================================================================

/**
 * Request body for POST /oauth-agent/login/start.
 */
interface LoginStartRequest {
  returnTo: string;
}

/**
 * Request body for POST /oauth-agent/logout.
 */
interface LogoutRequest {
  returnTo: string;
}

// =============================================================================
// Real Implementation
// =============================================================================

/**
 * Real implementation of AuthApiClient that makes HTTP requests to the BFF.
 *
 * Uses the HTTP wrapper from ./http.ts which handles:
 * - Static CSRF header injection (X-MP-Token-Handler: 1)
 * - JSON response parsing
 * - Error handling
 *
 * @example
 * ```typescript
 * const authClient = new RealAuthApiClient();
 *
 * // Start login
 * const { authorizationUrl } = await authClient.loginStart(window.location.href);
 * window.location.href = authorizationUrl;
 *
 * // Check auth state
 * const claims = await authClient.getClaims();
 * console.log('User:', claims.email);
 * ```
 */
export class RealAuthApiClient implements AuthApiClient {
  private readonly options?: HttpOptions;

  /**
   * Create a new RealAuthApiClient.
   *
   * @param options - Optional HTTP options (e.g., custom fetch for testing)
   */
  constructor(options?: HttpOptions) {
    this.options = options;
  }

  /**
   * POST /oauth-agent/login/start
   */
  async loginStart(returnTo: string): Promise<LoginStartResponse> {
    const body: LoginStartRequest = { returnTo };
    return httpPost<LoginStartResponse>('/oauth-agent/login/start', body, this.options);
  }

  /**
   * GET /oauth-agent/claims
   */
  async getClaims(): Promise<Claims> {
    return httpGet<Claims>('/oauth-agent/claims', this.options);
  }

  /**
   * GET /api/tenant-management/me
   */
  async getIdentity(): Promise<Identity> {
    return httpGet<Identity>('/api/tenant-management/me', this.options);
  }

  /**
   * GET /oauth-agent/userinfo
   */
  async getUserInfo(): Promise<Userinfo> {
    return httpGet<Userinfo>('/oauth-agent/userinfo', this.options);
  }

  /**
   * POST /oauth-agent/refresh
   */
  async refresh(): Promise<RefreshResponse> {
    return httpPost<RefreshResponse>('/oauth-agent/refresh', undefined, this.options);
  }

  /**
   * POST /oauth-agent/logout
   */
  async logout(returnTo: string): Promise<LogoutResponse> {
    const body: LogoutRequest = { returnTo };
    return httpPost<LogoutResponse>('/oauth-agent/logout', body, this.options);
  }
}

// =============================================================================
// Default Instance
// =============================================================================

/**
 * Default singleton instance of the Real Auth API client.
 *
 * For most use cases, import this directly:
 * ```typescript
 * import { authApiClient } from './api/AuthApiClient';
 * const claims = await authApiClient.getClaims();
 * ```
 */
export const authApiClient = new RealAuthApiClient();
